/* eslint-disable camelcase */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useLocation } from 'react-router-dom';
import { useMount } from 'react-use';

import * as auth from '@utils/auth';
import api from '@api';

const AuthContext = React.createContext();

const ACTION_TYPE = {
  SIGNING_IN: 'signingIn',
  SIGNED_IN: 'signedIn',
  SIGNED_IN_ANONYMOUS: 'signedInAnonymous',
  SIGNED_OUT: 'signedOut',
  SIGN_IN_FAILED: 'signInFailed',
  SET_NAME: 'setName',
};

const initialState = {
  isFetching: false,
  isAuthenticated: false,
  user: null,
  error: null,
};

const authReducer = (state, { type, payload, error }) => {
  switch (type) {
    case ACTION_TYPE.SIGNING_IN: {
      return { ...state, isFetching: true, error: null };
    }
    case ACTION_TYPE.SIGNED_IN: {
      return { user: payload, isAuthenticated: true, isFetching: false, error: null };
    }
    case ACTION_TYPE.SIGNED_IN_ANONYMOUS:
    case ACTION_TYPE.SIGNED_OUT: {
      return { ...initialState };
    }
    case ACTION_TYPE.SIGN_IN_FAILED: {
      return { ...initialState, error };
    }
    case ACTION_TYPE.SET_NAME: {
      return { ...state, user: { ...state.user, firstName: payload.firstName, lastName: payload.lastName } };
    }
    default: {
      throw new Error(`Unhandled action type: ${type}`);
    }
  }
};

const AuthProvider = ({ children }) => {
  const location = useLocation();
  const [state, dispatch] = React.useReducer(authReducer, initialState);

  useMount(async () => {
    dispatch({ type: ACTION_TYPE.SIGNING_IN });
    auth.configure();

    try {
      const [session, cognitoUser, apiUser] = await Promise.all([auth.getSession(), auth.getUser(), api.users.getMe()]);

      const userInformation = {
        firstName: cognitoUser?.attributes.given_name,
        lastName: cognitoUser?.attributes.family_name,
        event: apiUser?.event,
        role: apiUser?.cmsRole,
      };

      if (session && cognitoUser) {
        dispatch({ type: ACTION_TYPE.SIGNED_IN, payload: userInformation });
      } else {
        dispatch({ type: ACTION_TYPE.SIGNED_IN_ANONYMOUS });
      }
    } catch (error) {
      dispatch({ type: ACTION_TYPE.SIGN_IN_FAILED, error });
    }
  });

  React.useEffect(() => {
    // Check on each navigation if we are still logged in. If not - redirect to login
    auth.verifySignIn();
  }, [location]);

  const setName = async ({ given_name: firstName, family_name: lastName }) => {
    dispatch({
      type: ACTION_TYPE.SET_NAME,
      payload: { firstName, lastName },
    });
  };

  const signOut = React.useCallback(async () => {
    try {
      await auth.signOut();
      dispatch({ type: ACTION_TYPE.SIGNED_OUT });
    } catch (e) {
      console.log(e);
    }
  }, []);

  const value = React.useMemo(() => ({ ...state, signOut, setName }), [signOut, state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthProvider, AuthContext };
